import { useEffect, useMemo, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import WorkerRecordDrawer from "../workerRecord/WorkerRecordDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useFaceCheckDailyList, FACE_CHECK_DAILY_DEFAULT_PAGE_SIZE } from "./useFaceCheckDailyList.js";
import { getFaceCheckDailyDetail, reviewFaceCheckDaily, runFaceCheckMissingScan } from "./faceCheckApi.js";
import "./faceCheckDaily.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";
import RecordDrawerHeader from "../components/RecordDrawerHeader.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Safety", "Daily Face Check"];
const DRAWER_TITLE_ID = "fc-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;

const SUBTITLE_TEXT = "ตรวจสถานะการยืนยันตัวตนรายวันของแรงงาน เพื่อกันการฝากโทรศัพท์ให้คนอื่นสแกนแทน";

const POLICY_LINES = [
  "หน้านี้ไม่แสดงภาพใบหน้าของแรงงาน เพื่อความเป็นส่วนตัว",
  "ระบบไม่จับคู่ใบหน้าให้อัตโนมัติ ผลการตรวจมาจากการตัดสินของเจ้าหน้าที่เท่านั้น",
  "ตรวจหารายการที่ยังไม่ยืนยันซ้ำกี่ครั้งก็ได้ จะไม่เกิดรายการซ้ำ",
  "ระบบยังไม่แจ้งแรงงานหรือหัวหน้างานให้อัตโนมัติ ต้องติดต่อเอง",
];

const NO_RAW_IMAGE_NOTE = "หน้านี้ไม่แสดงภาพใบหน้า เพื่อความเป็นส่วนตัวของแรงงาน";

const MISSING_SCAN_WARNING_LINES = [
  "โหมดทดลองจะแสดงจำนวนเฉยๆ โดยไม่บันทึกอะไรเลย",
  "ใช้ได้เฉพาะ SuperAdmin",
  "ตรวจเฉพาะการยืนยันตัวตนรายวัน ไม่รวมพิกัดหรือสัญญาณจากเครื่อง",
];

const REVIEW_RULE_LINES = [
  "การตัดสินนี้ไม่มีการจับคู่ใบหน้าอัตโนมัติ — เป็นการตัดสินของเจ้าหน้าที่ตรวจเท่านั้น",
  "ทุกการตัดสินถูกบันทึกไว้ พร้อมชื่อผู้ตรวจสอบและเวลา ย้อนกลับดูภายหลังได้",
  "การตัดสินว่าไม่ผ่านหรือต้องแก้ไข จะไม่สร้างรายการผิดปกติให้โดยอัตโนมัติ",
];

const COMPLIANCE_STATUS_OPTIONS = ["SubmittedPendingReview", "AttentionRequired", "Accepted", "Rejected", "NeedsCorrection"];
const COMPLIANCE_STATUS_LABELS = {
  SubmittedPendingReview: "Submitted Pending Review",
  AttentionRequired: "Attention Required",
  Accepted: "Accepted",
  Rejected: "Rejected",
  NeedsCorrection: "Needs Correction",
};
const COMPLIANCE_BADGE_VARIANT = {
  SubmittedPendingReview: "info",
  AttentionRequired: "warning",
  Accepted: "success",
  Rejected: "danger",
  NeedsCorrection: "neutral",
};

const DECISION_OPTIONS = [
  { value: "Accepted", label: "Accepted", labelTh: "ยอมรับ" },
  { value: "Rejected", label: "Rejected", labelTh: "ไม่ผ่าน" },
  { value: "NeedsCorrection", label: "Needs Correction", labelTh: "ต้องแก้ไข" },
];

const SIGNAL_BADGE_VARIANT = { Passed: "success", Failed: "danger", NotAvailable: "neutral" };
const SIGNAL_LABELS = { Passed: "ผ่าน", Failed: "ไม่ผ่าน" };

// The audit log stores every action under its internal name and packs the
// details into a "Key=Value;Key=Value" string. That shape is right for a
// forensic record and wrong for a human: operators were reading
// "MobileFaceCheckSubmitted / WorkerId=...;WasNewRow=False" off the screen.
// These maps turn the log into sentences; anything unmapped is dropped rather
// than printed raw.
const HISTORY_ACTION_LABELS = {
  MobileFaceCheckSubmitted: "แรงงานส่งการยืนยันตัวตน",
  MobileFaceCheckPhotoStored: "แนบภาพยืนยันตัวตน",
  MobileFaceCheckMatchUnavailable: "ระบบบันทึกภาพไม่สำเร็จ",
  FaceCheckReviewed: "เจ้าหน้าที่ตรวจสอบแล้ว",
  FaceCheckReviewForbidden: "มีผู้พยายามตรวจสอบโดยไม่มีสิทธิ์",
  FaceCheckMissingScan: "ตรวจหารายการที่ยังไม่ยืนยัน",
  FaceCheckMissingAbnormalCreated: "บันทึกว่ายังไม่ยืนยันตัวตน",
};
const HISTORY_ACTOR_ROLE_LABELS = { Worker: "แรงงาน", System: "ระบบ" };

function complianceLabel(value) {
  return COMPLIANCE_STATUS_LABELS[value] ?? value ?? "-";
}

function complianceBadgeVariant(value) {
  return COMPLIANCE_BADGE_VARIANT[value] ?? "neutral";
}

function signalBadgeVariant(value) {
  return SIGNAL_BADGE_VARIANT[value] ?? "neutral";
}

function hasRealSignal(value) {
  return Boolean(SIGNAL_LABELS[value]);
}

function historyActionLabel(value) {
  const known = HISTORY_ACTION_LABELS[value];
  if (known) {
    return known;
  }
  // A future action nobody has translated yet: space out the internal name so
  // it at least reads as words, never as a bare identifier.
  const spaced = String(value ?? "").replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim();
  return spaced.length > 0 ? spaced : "มีการเปลี่ยนแปลง";
}

function historyActorLabel(role) {
  const trimmed = typeof role === "string" ? role.trim() : "";
  if (trimmed.length === 0) {
    return "";
  }
  // SuperAdmin / Agent / SuperAgent / PLC are role names this product shows
  // everywhere, so they pass through unchanged.
  return HISTORY_ACTOR_ROLE_LABELS[trimmed] ?? trimmed;
}

function parseAuditSummary(summary) {
  const text = typeof summary === "string" ? summary.trim() : "";
  if (text.length === 0) {
    return { fields: {}, note: "" };
  }
  // ReviewNote is free text a human typed and may itself contain ";" or "=",
  // so it is taken as the whole remainder instead of being split.
  const noteAt = text.indexOf("ReviewNote=");
  const head = noteAt === -1 ? text : text.slice(0, noteAt);
  const note = noteAt === -1 ? "" : text.slice(noteAt + "ReviewNote=".length).trim();
  const fields = {};
  head.split(";").forEach((pair) => {
    const at = pair.indexOf("=");
    if (at > 0) {
      fields[pair.slice(0, at).trim()] = pair.slice(at + 1).trim();
    }
  });
  return { fields, note };
}

function historyDetailLines(summary) {
  const { fields, note } = parseAuditSummary(summary);
  const lines = [];
  if (fields.ComplianceStatus) {
    lines.push(`ผลการตรวจ: ${complianceLabel(fields.ComplianceStatus)}`);
  }
  if (hasRealSignal(fields.LivenessSignal)) {
    lines.push(`การตรวจใบหน้าสด: ${SIGNAL_LABELS[fields.LivenessSignal]}`);
  }
  if (hasRealSignal(fields.DeviceAttestationSignal)) {
    lines.push(`การตรวจเครื่องที่ใช้: ${SIGNAL_LABELS[fields.DeviceAttestationSignal]}`);
  }
  if (fields.HasFaceCaptureReference === "True") {
    lines.push("แนบภาพยืนยันตัวตนมาด้วย");
  }
  if (note) {
    lines.push(`หมายเหตุ: ${note}`);
  }
  // Everything else in the summary - the worker id, the date, whether the row
  // was an insert or an update - the operator already knows or cannot act on.
  return lines;
}

function todayThaiDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} UTC`;
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงรายการ Face Check นี้";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูรายละเอียด Face Check นี้";
  }
  if (error?.status === 404) {
    return "ไม่พบ Face Check นี้ อาจถูกลบหรือย้ายแล้ว";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

function describeReviewError(error) {
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ตรวจสอบรายการนี้ (มุมมองอย่างเดียวสำหรับ SuperAgent/PLC)";
  }
  if (error?.status === 404) {
    return "ไม่พบ Face Check นี้ อาจถูกลบหรือย้ายแล้ว";
  }
  if (error?.status === 400) {
    return "กรุณาตรวจสอบผลการตรวจและหมายเหตุอีกครั้ง";
  }
  return "ไม่สามารถบันทึกผลการตรวจสอบได้ กรุณาลองใหม่อีกครั้ง";
}

function describeMissingScanError(error) {
  if (error?.status === 403) {
    return "เฉพาะ SuperAdmin เท่านั้นที่สามารถเรียกใช้ Missing Scan ได้";
  }
  if (error?.status === 400) {
    return "กรุณาตรวจสอบ thaiDate อีกครั้ง (รูปแบบ yyyy-MM-dd)";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  return "ไม่สามารถเรียกใช้ Missing Scan ได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * /backoffice/face-check/daily - the Backoffice Daily Face Check screen
 * (Prompt 123B, built from the W59 reference). Entirely over the Prompt 122
 * Daily Face Check API: a paginated, scope-isolated list of
 * dbo.WmxFaceCheckEvents rows (GET .../face-check/daily), a single-event
 * detail (GET .../face-check/daily/{faceCheckEventId}), a review decision
 * (POST .../face-check/daily/{faceCheckEventId}/review), and a
 * SuperAdmin-only missing-scan (POST .../face-check/daily/missing-scan).
 *
 * Daily Face Check confirms the worker using the mobile app is present and
 * submitting a daily identity/liveness check - a compliance control against
 * a worker leaving a phone with someone else, never a real biometric
 * face-matching product. This screen never calls an external
 * face-recognition provider, never renders a raw face image or a raw
 * storage key (only hasFaceCaptureReference), never implements SOS, never
 * sends a real notification (notificationDeliveryStarted is always rendered
 * false with the backend's own fixed reserved-reason string), and never
 * implies automatic worker punishment/blocking - a review decision only ever
 * updates complianceStatus for human record-keeping, and a missing-scan run
 * only ever creates an Open FaceCheckMissing abnormal event for human
 * review. Precise latitude/longitude are intentionally never rendered here
 * even though FaceCheckDailyDetailDto carries them - this screen's purpose
 * is identity/liveness compliance, not location tracking (that is
 * /backoffice/tracking/latest's own screen).
 */
export default function FaceCheckDailyPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;

  const [dateFilter, setDateFilter] = useState("");
  const [complianceStatusFilter, setComplianceStatusFilter] = useState("");
  const [superAgentOrgInput, setSuperAgentOrgInput] = useState("");
  const [plcOrgInput, setPlcOrgInput] = useState("");
  const [agentIdInput, setAgentIdInput] = useState("");
  const [employerIdInput, setEmployerIdInput] = useState("");
  const [workerIdInput, setWorkerIdInput] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  // Clicking the row opens the ONE shared worker record; the row's own
  // action button keeps opening this screen's process detail.
  const [workerRecordId, setWorkerRecordId] = useState(null);

  const [selectedFaceCheckEventId, setSelectedFaceCheckEventId] = useState(null);
  const [drawerMode, setDrawerMode] = useState(null); // null | "detail" | "review" | "missing-scan"
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });

  const [reviewDecision, setReviewDecision] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [reviewValidationError, setReviewValidationError] = useState(null);
  const [reviewSubmitState, setReviewSubmitState] = useState({ status: "idle", error: null });

  const [missingScanThaiDate, setMissingScanThaiDate] = useState(todayThaiDate());
  const [missingScanDryRun, setMissingScanDryRun] = useState(true);
  const [missingScanState, setMissingScanState] = useState({ status: "idle", error: null, result: null });

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [dateFilter, complianceStatusFilter, superAgentOrgInput, plcOrgInput, agentIdInput, employerIdInput, workerIdInput, search]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: FACE_CHECK_DAILY_DEFAULT_PAGE_SIZE,
      date: dateFilter,
      complianceStatus: complianceStatusFilter,
      superAgentOrganizationId: superAgentOrgInput,
      plcOrganizationId: plcOrgInput,
      agentId: agentIdInput,
      employerId: employerIdInput,
      workerId: workerIdInput,
      search,
    }),
    [page, dateFilter, complianceStatusFilter, superAgentOrgInput, plcOrgInput, agentIdInput, employerIdInput, workerIdInput, search]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useFaceCheckDailyList(filters);

  // Detail is fetched whenever selectedFaceCheckEventId changes (row/card
  // opened) - the review subview reuses the same fetch, it never issues a
  // second GET just to switch views within the same open drawer.
  useEffect(() => {
    if (!selectedFaceCheckEventId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState({ status: "loading", data: null, error: null, forId: selectedFaceCheckEventId });

    getFaceCheckDailyDetail(selectedFaceCheckEventId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedFaceCheckEventId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedFaceCheckEventId });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedFaceCheckEventId]);

  function openDetail(faceCheckEventId) {
    setSelectedFaceCheckEventId(faceCheckEventId);
    setDrawerMode("detail");
    setReviewDecision("");
    setReviewNote("");
    setReviewValidationError(null);
    setReviewSubmitState({ status: "idle", error: null });
  }

  function openMissingScanDrawer() {
    setMissingScanState({ status: "idle", error: null, result: null });
    setDrawerMode("missing-scan");
  }

  function closeDrawer() {
    setDrawerMode(null);
    setSelectedFaceCheckEventId(null);
  }

  function handleRefresh() {
    refetchList();
  }

  function handleReviewSubmit() {
    const needsNote = reviewDecision === "Rejected" || reviewDecision === "NeedsCorrection";
    if (!reviewDecision) {
      setReviewValidationError("กรุณาเลือกผลการตรวจสอบ (decision)");
      return;
    }
    if (needsNote && !reviewNote.trim()) {
      setReviewValidationError("กรุณาระบุหมายเหตุการตรวจ เมื่อผลการตรวจเป็น Rejected หรือ Needs Correction");
      return;
    }

    setReviewValidationError(null);
    setReviewSubmitState({ status: "submitting", error: null });

    reviewFaceCheckDaily(selectedFaceCheckEventId, { decision: reviewDecision, reviewNote: reviewNote.trim() || undefined })
      .then((refreshedDetail) => {
        setReviewSubmitState({ status: "success", error: null });
        setDetailState({ status: "ready", data: refreshedDetail, error: null, forId: selectedFaceCheckEventId });
        refetchList();
        setDrawerMode("detail");
      })
      .catch((error) => {
        setReviewSubmitState({ status: "error", error });
      });
  }

  function handleMissingScanSubmit() {
    if (!missingScanThaiDate) {
      setMissingScanState({ status: "error", error: { status: 400 }, result: null });
      return;
    }
    setMissingScanState((previous) => ({ status: "submitting", error: null, result: previous.result }));
    runFaceCheckMissingScan({ thaiDate: missingScanThaiDate, dryRun: missingScanDryRun })
      .then((result) => {
        setMissingScanState({ status: "success", error: null, result });
        // A dryRun result never fakes created events - only a real
        // createdCount > 0 (which a dryRun response never has, per the
        // backend's own contract) is worth refetching the list for.
        if (result?.createdCount > 0) {
          refetchList();
        }
      })
      .catch((error) => {
        setMissingScanState((previous) => ({ status: "error", error, result: previous.result }));
      });
  }

  const items = listData?.items ?? [];
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / FACE_CHECK_DAILY_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(
    dateFilter || complianceStatusFilter || superAgentOrgInput || plcOrgInput || agentIdInput || employerIdInput || workerIdInput || search
  );

  const isInitialLoading = listStatus === "loading" && listData === null;
  const isInitialError = listStatus === "error" && listData === null;

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="table" label="กำลังโหลดรายการ Face Check..." />
    </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดรายการ Face Check ได้</h1>
          <p className="wx-state-body">{describeListError(listError)}</p>
          <button type="button" className="wx-button wx-button--primary" onClick={handleRefresh}>
            ลองใหม่อีกครั้ง
          </button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-fc-page">
        <header className="wx-fc-head">
          <div className="wx-fc-title">
            <h1>Daily Face Check</h1>
            <p>{SUBTITLE_TEXT}</p>
          </div>
          {primaryRole && (
            <span className="wx-fc-scope-pill">
              Scope <b>{primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-fc-policy-banner" role="status">
          <div className="wx-fc-policy-banner-title">
            <span className="wx-fc-policy-dot" aria-hidden="true" />
            นโยบาย Daily Face Check
          </div>
          <ul>
            {POLICY_LINES.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>

        {/* A row of five KPI tiles stood here, every one of them rendering the
            word "สงวนไว้" over a pill reading "รอ Summary API" - a whole band of
            the page, on every visit, reporting that we had not built a summary
            endpoint. No number was ever shown. The list below is the real
            content; it is now the first thing under the header. */}

        <div className="wx-fc-toolbar">
          <label className="wx-fc-search">
            <span className="wx-visually-hidden">ค้นหารหัส Face Check หรือรหัสแรงงาน</span>
            <input
              type="search"
              placeholder="ค้นหารหัส Face Check หรือรหัสแรงงาน"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <div className="wx-fc-toolbar-actions">
            <button type="button" className="wx-button wx-button--ghost wx-fc-refresh-button" onClick={handleRefresh}>
              Refresh
            </button>
            <button type="button" className="wx-button wx-button--primary wx-fc-missing-scan-button" onClick={openMissingScanDrawer}>
              Missing Scan
            </button>
          </div>

          <div className="wx-fc-filters">
            <label className="wx-fc-filter">
              <span>Date</span>
              <input type="date" value={dateFilter} onChange={(event) => setDateFilter(event.target.value)} />
            </label>

            <label className="wx-fc-filter">
              <span>Compliance</span>
              <select value={complianceStatusFilter} onChange={(event) => setComplianceStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {COMPLIANCE_STATUS_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {complianceLabel(value)}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-fc-filter">
              <span>Super Agent Org</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={superAgentOrgInput} onChange={(event) => setSuperAgentOrgInput(event.target.value)} />
            </label>

            <label className="wx-fc-filter">
              <span>PLC Org</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={plcOrgInput} onChange={(event) => setPlcOrgInput(event.target.value)} />
            </label>

            <label className="wx-fc-filter">
              <span>Agent</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={agentIdInput} onChange={(event) => setAgentIdInput(event.target.value)} />
            </label>

            <label className="wx-fc-filter">
              <span>นายจ้าง</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={employerIdInput} onChange={(event) => setEmployerIdInput(event.target.value)} />
            </label>

            <label className="wx-fc-filter">
              <span>แรงงาน</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={workerIdInput} onChange={(event) => setWorkerIdInput(event.target.value)} />
            </label>
          </div>
        </div>

        <section className="wx-panel wx-fc-list-panel">
          <div className="wx-fc-list-head">
            <h2 className="wx-panel-title">รายการ Face Check รายวัน</h2>
            <span className="wx-fc-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-fc-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {items.length === 0 ? (
            <p className="wx-empty-list">{hasActiveFilters ? "ไม่พบรายการที่ตรงกับตัวกรองหรือคำค้นหา" : "ยังไม่มี Face Check ในขอบเขตที่มองเห็น"}</p>
          ) : (
            <>
              <div className="wx-fc-table-wrap">
                <table className="wx-fc-table">
                  <colgroup>
                    {/* 10 cols for 10 columns, summing to 100. It had FOURTEEN.
                        table-layout:fixed reads these in order and ignores
                        any extra, so a mismatched or short list sizes the
                        real columns off something that was never about
                        them. If a column is added or removed, change this
                        list too. */}
                    <col style={{ width: "12%" }} /><col style={{ width: "9%" }} /><col style={{ width: "10%" }} /><col style={{ width: "10%" }} />
                    <col style={{ width: "9%" }} /><col style={{ width: "8%" }} /><col style={{ width: "10%" }} /><col style={{ width: "10%" }} />
                    <col style={{ width: "12%" }} /><col style={{ width: "10%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>แรงงาน</th>
                      <th>รหัสงาน</th>
                      <th>นายจ้าง</th>
                      <th>Agent</th>
                      <th>วันที่</th>
                      <th>Face Check</th>
                      <th>ส่งเมื่อ</th>
                      <th>สถานะ</th>
                      {/* No live-face / device-check columns: the app has never
                          sent either signal, so both read "not collected" on
                          every row. The drawer shows them if one ever lands. */}
                      <th>ภาพยืนยัน</th>
                      <th aria-hidden="true">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.faceCheckEventId} className="wx-fc-row" onClick={() => setWorkerRecordId(item.workerId)}>
                        <td>
                          <span className="wx-fc-cell-strong">{item.workerName ?? "-"}</span>
                          <span className="wx-fc-cell-sub">{item.workerCode ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-fc-code">{item.assignmentId ?? "-"}</span>
                        </td>
                        <td>{item.employerName ?? "-"}</td>
                        <td>
                          <span className="wx-fc-cell-strong">{item.agentName ?? "-"}</span>
                          <span className="wx-fc-cell-sub">{item.agentCode ?? "-"}</span>
                        </td>
                        <td>{item.thaiDate}</td>
                        <td>
                          <span className="wx-fc-code">{item.faceCheckCode ?? item.faceCheckEventId}</span>
                        </td>
                        <td>{formatDateTimeTh(item.submittedAtUtc) ?? "-"}</td>
                        <td>
                          <span className={`wx-badge wx-badge--${complianceBadgeVariant(item.complianceStatus)}`}>{complianceLabel(item.complianceStatus)}</span>
                        </td>
                        <td>
                          {item.hasFaceCaptureReference ? (
                            <span className="wx-badge wx-badge--success">แนบมาแล้ว</span>
                          ) : (
                            <span className="wx-badge wx-badge--neutral">ไม่ได้แนบ</span>
                          )}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="wx-fc-row-action"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDetail(item.faceCheckEventId);
                            }}
                          >
                            เปิดรายละเอียด
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wx-fc-cards">
                {items.map((item) => (
                  <FaceCheckCard key={item.faceCheckEventId} item={item} onOpen={() => openDetail(item.faceCheckEventId)} />
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      </div>

      <DetailDrawer
        open={drawerMode !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-fc-row, .wx-fc-card, .wx-fc-refresh-button, .wx-fc-missing-scan-button"
      >
        {drawerMode === "detail" && (
          <DetailView
            titleId={DRAWER_TITLE_ID}
            state={detailState}
            onReview={() => {
              setReviewDecision("");
              setReviewNote("");
              setReviewValidationError(null);
              setReviewSubmitState({ status: "idle", error: null });
              setDrawerMode("review");
            }}
          />
        )}
        {drawerMode === "review" && (
          <ReviewDrawerView
            titleId={DRAWER_TITLE_ID}
            detail={detailState.data}
            decision={reviewDecision}
            onDecisionChange={setReviewDecision}
            note={reviewNote}
            onNoteChange={setReviewNote}
            validationError={reviewValidationError}
            submitState={reviewSubmitState}
            onSubmit={handleReviewSubmit}
            onBack={() => setDrawerMode("detail")}
          />
        )}
        {drawerMode === "missing-scan" && (
          <MissingScanDrawerView
            titleId={DRAWER_TITLE_ID}
            thaiDate={missingScanThaiDate}
            onThaiDateChange={setMissingScanThaiDate}
            dryRun={missingScanDryRun}
            onDryRunChange={setMissingScanDryRun}
            submitState={missingScanState}
            onSubmit={handleMissingScanSubmit}
          />
        )}
      </DetailDrawer>
      
        <WorkerRecordDrawer
        open={workerRecordId !== null}
        workerId={workerRecordId}
        onClose={() => setWorkerRecordId(null)}
        selectableSelector="tr.wx-fc-row, .wx-fc-card"
      />
    </AppShell>
  );
}

function FaceCheckCard({ item, onOpen }) {
  return (
    <div className="wx-fc-card" onClick={onOpen}>
      <div className="wx-fc-card-top">
        <div>
          <strong>{item.workerName ?? "-"}</strong>
          <span className="wx-fc-card-sub">
            {item.workerCode ?? "-"} · {item.thaiDate}
          </span>
        </div>
        <div className="wx-fc-card-tags">
          <span className={`wx-badge wx-badge--${complianceBadgeVariant(item.complianceStatus)}`}>{complianceLabel(item.complianceStatus)}</span>
        </div>
      </div>
      <div className="wx-fc-card-grid">
        <div>
          <label>ภาพยืนยัน</label>
          <strong>
            {item.hasFaceCaptureReference ? (
              <span className="wx-badge wx-badge--success">แนบมาแล้ว</span>
            ) : (
              <span className="wx-badge wx-badge--neutral">ไม่ได้แนบ</span>
            )}
          </strong>
        </div>
      </div>
      <button
        type="button"
        className="wx-fc-row-action wx-fc-row-action--full"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        เปิดรายละเอียด
      </button>
    </div>
  );
}

function DetailView({ titleId, state, onReview }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-fc-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const nextActions = detail.nextActions ?? {};
  const reviewHistory = detail.reviewHistory ?? [];

  return (
    <div className="wx-fc-drawer">
      <RecordDrawerHeader
        titleId={titleId}
        avatarText={detail.workerName}
        title={detail.workerName ?? "-"}
        subtitle={`${detail.workerCode ?? "-"} · ${detail.faceCheckCode ?? detail.faceCheckEventId}`}
        badges={[
          { label: complianceLabel(detail.complianceStatus), variant: complianceBadgeVariant(detail.complianceStatus) },
          { label: detail.thaiDate, variant: "neutral" },
        ]}
      />

      <section className="wx-fc-dsec">
        <h3>ห่วงโซ่ความรับผิดชอบ</h3>
        <div className="wx-fc-kv-row">
          <span>แรงงาน</span>
          <strong>
            {detail.workerName ?? "-"} · {detail.workerCode ?? "-"}
          </strong>
        </div>
        <div className="wx-fc-kv-row">
          <span>รหัสงาน</span>
          <strong className="wx-fc-code">{detail.assignmentId ?? "-"}</strong>
        </div>
        <div className="wx-fc-kv-row">
          <span>นายจ้าง</span>
          <strong>{detail.employerName ?? "-"}</strong>
        </div>
        <div className="wx-fc-kv-row">
          <span>Agent</span>
          <strong>
            {detail.agentName ?? "-"} · {detail.agentCode ?? "-"}
          </strong>
        </div>
      </section>

      <section className="wx-fc-dsec">
        <h3>ข้อมูลการส่งตรวจ</h3>
        <div className="wx-fc-kv-row">
          <span>วันที่ตรวจ</span>
          <strong>{detail.thaiDate}</strong>
        </div>
        <div className="wx-fc-kv-row">
          <span>เวลาที่ส่ง</span>
          <strong>{formatDateTimeTh(detail.submittedAtUtc) ?? "-"}</strong>
        </div>
        <div className="wx-fc-kv-row">
          <span>สถานะ</span>
          <strong>
            <span className={`wx-badge wx-badge--${complianceBadgeVariant(detail.complianceStatus)}`}>{complianceLabel(detail.complianceStatus)}</span>
          </strong>
        </div>
        {/* The two signal rows used to render on every record, almost always
            reading "NotAvailable" - a row whose only message was that we had
            not collected it. They now appear only when a real result exists. */}
        {hasRealSignal(detail.livenessSignal) && (
          <div className="wx-fc-kv-row">
            <span>การตรวจใบหน้าสด</span>
            <strong>
              <span className={`wx-badge wx-badge--${signalBadgeVariant(detail.livenessSignal)}`}>{SIGNAL_LABELS[detail.livenessSignal]}</span>
            </strong>
          </div>
        )}
        {hasRealSignal(detail.deviceAttestationSignal) && (
          <div className="wx-fc-kv-row">
            <span>การตรวจเครื่องที่ใช้</span>
            <strong>
              <span className={`wx-badge wx-badge--${signalBadgeVariant(detail.deviceAttestationSignal)}`}>{SIGNAL_LABELS[detail.deviceAttestationSignal]}</span>
            </strong>
          </div>
        )}
      </section>

      <section className="wx-fc-dsec">
        <h3>ความเป็นส่วนตัวของภาพใบหน้า</h3>
        <div className="wx-fc-kv-row">
          <span>ภาพยืนยันตัวตน</span>
          <strong>
            {detail.hasFaceCaptureReference ? (
              <span className="wx-badge wx-badge--success">แนบมาแล้ว</span>
            ) : (
              <span className="wx-badge wx-badge--neutral">ไม่ได้แนบ</span>
            )}
          </strong>
        </div>
        <p className="wx-fc-note-muted wx-fc-note-muted--left">{NO_RAW_IMAGE_NOTE}</p>
      </section>

      <section className="wx-fc-dsec">
        <h3>ประวัติการตรวจสอบ</h3>
        {reviewHistory.length === 0 ? (
          <p className="wx-fc-note-muted wx-fc-note-muted--left">ยังไม่มีการตรวจสอบสำหรับรายการนี้</p>
        ) : (
          <div className="wx-fc-history-list">
            {reviewHistory.map((entry, index) => {
              const actor = historyActorLabel(entry.actorRole);
              const lines = historyDetailLines(entry.summary);
              return (
                <div className="wx-fc-history-row" key={`${entry.action}-${entry.occurredAtUtc}-${index}`}>
                  <div className="wx-fc-history-row-top">
                    <strong>{historyActionLabel(entry.action)}</strong>
                    <span>{formatDateTimeTh(entry.occurredAtUtc) ?? "-"}</span>
                  </div>
                  {actor && <p className="wx-fc-history-actor">โดย {actor}</p>}
                  {lines.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="wx-fc-dsec">
        <h3>การตรวจสอบ</h3>
        <button type="button" className="wx-fc-row-action wx-fc-row-action--full" disabled={!nextActions.canReview} onClick={onReview}>
          ตรวจสอบ (Review)
        </button>
        {/* The note used to print the permission flag on every record. It now
            appears only when the button is actually blocked, and says who to
            ask instead of which flag was false. */}
        {!nextActions.canReview && (
          <p className="wx-fc-note-muted wx-fc-note-muted--left">
            เฉพาะ SuperAdmin หรือ Agent เจ้าของงานเท่านั้นที่ตรวจสอบรายการนี้ได้ SuperAgent และ PLC ดูได้อย่างเดียว
          </p>
        )}
      </section>

      <div className="wx-fc-notice">ผลการตรวจนี้มาจากการตัดสินของเจ้าหน้าที่ ไม่ใช่การลงโทษแรงงานโดยอัตโนมัติ</div>
    </div>
  );
}

function ReviewDrawerView({ titleId, detail, decision, onDecisionChange, note, onNoteChange, validationError, submitState, onSubmit, onBack }) {
  const submitting = submitState.status === "submitting";

  return (
    <div className="wx-fc-drawer">
      <span className="wx-fc-drawer-kicker">REVIEW</span>
      <h2 id={titleId} className="wx-fc-drawer-title">
        ตรวจสอบ Face Check
      </h2>
      <p className="wx-fc-drawer-sub">บันทึกผลการตรวจของเจ้าหน้าที่ลงในรายการนี้</p>
      <div className="wx-fc-drawer-status">
        <span className="wx-badge wx-badge--neutral">{detail?.faceCheckCode ?? detail?.faceCheckEventId ?? "-"}</span>
        <span className="wx-badge wx-badge--neutral">{detail?.workerName ?? "-"}</span>
      </div>

      <section className="wx-fc-dsec">
        <h3>
          ผลการตรวจ <b>*</b>
        </h3>
        <div className="wx-fc-decision-picker" role="radiogroup" aria-label="ผลการตรวจ">
          {DECISION_OPTIONS.map((option) => (
            <label className="wx-fc-decision-opt" key={option.value}>
              <input
                type="radio"
                name="fc-decision"
                value={option.value}
                aria-label={option.label}
                checked={decision === option.value}
                onChange={() => onDecisionChange(option.value)}
              />
              <span className="wx-fc-decision-opt-face">
                {option.label}
                <br />
                <span className="wx-fc-decision-opt-th">{option.labelTh}</span>
              </span>
            </label>
          ))}
        </div>

        <label className="wx-fc-field">
          <span>
            หมายเหตุการตรวจ{" "}
            {(decision === "Rejected" || decision === "NeedsCorrection") && <b>*</b>}{" "}
            <span className="wx-fc-optional">(บังคับเมื่อ Rejected/NeedsCorrection)</span>
          </span>
          <textarea
            aria-label="หมายเหตุการตรวจ"
            value={note}
            onChange={(event) => onNoteChange(event.target.value)}
            placeholder="ระบุเหตุผลของการตัดสิน"
          />
        </label>
        <span className="wx-fc-field-hint">ต้องระบุเมื่อเลือก Rejected หรือ NeedsCorrection · ไม่เกิน 400 ตัวอักษร</span>
      </section>

      <div className="wx-fc-notice wx-fc-notice--warn">
        {REVIEW_RULE_LINES.map((line) => (
          <span key={line} className="wx-fc-notice-line">
            {line}
          </span>
        ))}
      </div>

      {validationError && (
        <p className="wx-fc-form-message" role="alert">
          {validationError}
        </p>
      )}

      {submitState.status === "error" && (
        <p className="wx-fc-form-message" role="alert">
          {describeReviewError(submitState.error)}
        </p>
      )}

      <div className="wx-fc-drawer-actions">
        <button type="button" className="wx-button wx-button--ghost" onClick={onBack} disabled={submitting}>
          ย้อนกลับ
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onSubmit} disabled={submitting}>
          {submitting ? "กำลังบันทึก..." : "บันทึกผลการตรวจสอบ"}
        </button>
      </div>
    </div>
  );
}

function MissingScanDrawerView({ titleId, thaiDate, onThaiDateChange, dryRun, onDryRunChange, submitState, onSubmit }) {
  const submitting = submitState.status === "submitting";
  const result = submitState.result;

  return (
    <div className="wx-fc-drawer">
      <span className="wx-fc-drawer-kicker">MISSING SCAN</span>
      <h2 id={titleId} className="wx-fc-drawer-title">
        สแกนหาการขาดส่ง Face Check
      </h2>
      <p className="wx-fc-drawer-sub">หาแรงงานที่ยังไม่ยืนยันตัวตนของวันที่เลือก</p>

      <section className="wx-fc-dsec">
        <h3>เงื่อนไขการตรวจ</h3>
        <label className="wx-fc-field">
          <span>
            วันที่ตรวจ <b>*</b>
          </span>
          <input type="date" aria-label="วันที่ตรวจ" value={thaiDate} onChange={(event) => onThaiDateChange(event.target.value)} />
        </label>
        <label className="wx-fc-field wx-fc-toggle-field">
          <span>โหมดทดลอง (ดูผลอย่างเดียว)</span>
          <span className="wx-fc-toggle">
            <input type="checkbox" aria-label="โหมดทดลอง" checked={dryRun} onChange={(event) => onDryRunChange(event.target.checked)} />
            <span className="wx-fc-toggle-track">
              <span className="wx-fc-toggle-thumb" />
            </span>
          </span>
        </label>
        <span className="wx-fc-field-hint">เปิดโหมดทดลองไว้เพื่อดูจำนวนที่จะได้รับผลกระทบก่อน โดยยังไม่บันทึกอะไร</span>
        <button type="button" className="wx-button wx-button--primary wx-fc-run-scan-button" disabled={submitting} onClick={onSubmit}>
          {submitting ? "กำลังสแกน..." : "เริ่มสแกน"}
        </button>
      </section>

      {submitState.status === "error" && (
        <p className="wx-fc-form-message" role="alert">
          {describeMissingScanError(submitState.error)}
        </p>
      )}

      {result && (
        <section className="wx-fc-dsec">
          <h3>ผลลัพธ์</h3>
          <div className="wx-fc-kv-row">
            <span>วันที่ตรวจ</span>
            <strong>{result.thaiDate}</strong>
          </div>
          <div className="wx-fc-kv-row">
            <span>โหมดทดลอง</span>
            <strong>{result.dryRun ? "ใช่" : "ไม่ใช่"}</strong>
          </div>
          <div className="wx-fc-kv-row">
            <span>ตรวจทั้งหมด</span>
            <strong>{result.scannedCount}</strong>
          </div>
          <div className="wx-fc-kv-row">
            <span>เข้าข่ายยังไม่ยืนยัน</span>
            <strong>{result.candidateCount}</strong>
          </div>
          <div className="wx-fc-kv-row">
            <span>บันทึกใหม่</span>
            <strong>{result.createdCount}</strong>
          </div>
          <div className="wx-fc-kv-row">
            <span>ข้าม (มีรายการค้างอยู่แล้ว)</span>
            <strong>{result.skippedOpenDuplicateCount}</strong>
          </div>
          <div className="wx-fc-kv-row">
            <span>ผู้ที่ต้องแจ้ง</span>
            <strong>{result.recipientPreparedCount}</strong>
          </div>
          {/* The delivery flag is false on every run and the reserved reason
              named a future packet. The operator only needs the instruction. */}
          <div className="wx-fc-notice">ระบบยังไม่แจ้งให้อัตโนมัติ กรุณาติดต่อแรงงานหรือหัวหน้างานเอง</div>
        </section>
      )}

      <div className="wx-fc-notice wx-fc-notice--warn">
        {MISSING_SCAN_WARNING_LINES.map((line) => (
          <span key={line} className="wx-fc-notice-line">
            {line}
          </span>
        ))}
      </div>
    </div>
  );
}
